// react
import { useMemo } from 'react';

// types
import { Pagination, Transaction } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Grid } from '@mui/material';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardReimbursement } from '../../../cards/CardReimbursement/CardReimbursement';

// fields
import { SelectTransactionPage } from '../../../fields/SelectTransactionPage/SelectTransactionPage';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// ui
import { EmptyProps } from '../../../ui/Empty/Empty';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';

// props
export interface TableReimbursementsProps {
	hasSearch?: boolean;
	hasSelectPage?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchTransactions: () => void;
	transactions: Transaction[];
}

export const TableReimbursements = ({
	hasSearch = true,
	hasSelectPage = true,
	isLoading = false,
	pagination,
	refetchTransactions,
	transactions,
}: TableReimbursementsProps) => {
	// hooks
	const { t } = useTranslation();

	// headers
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 3,
			},
			label: t('date'),
			value: 'date',
		},
		{
			GridProps: {
				lg: 2,
			},
			label: t('toAccount'),
			value: 'toAccount',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('amount'),
			value: 'amount',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('applied'),
			value: 'applied',
		},
		{
			GridProps: {
				lg: 2.5,
			},
			label: t('updated'),
			value: 'modified',
		},
		{
			GridProps: {
				lg: true,
			},
			label: '',
			value: 'actions',
		},
	];

	// empty
	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			content: { children: t('empty.reimbursements.content') },
			IconCircleProps: {
				color: 'grey',
				type: 'reimbursement',
			},
			title: { children: t('empty.reimbursements.title') },
		};

		return emptyProps;
	}, [t]);

	// content
	const content = useMemo(() => {
		if (isLoading || !transactions.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{transactions.map((transaction) => (
					<Grid item key={transaction.id} xs={12}>
						<CardReimbursement
							refetchTransactions={refetchTransactions}
							transaction={transaction}
						/>
					</Grid>
				))}
			</Grid>
		);
	}, [empty, isLoading, refetchTransactions, transactions]);

	return (
		<Grid container spacing={2}>
			{hasSelectPage && (
				<Grid item xs={12} md={3} lg={2}>
					<SelectTransactionPage value="reimbursements" />
				</Grid>
			)}
			{hasSearch && (
				<Grid item xs={12} md={true}>
					<FormSearch InputProps={{ placeholder: t('searchById') }} />
				</Grid>
			)}
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={transactions.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
